import React, { useEffect, useState } from 'react';
import LiveVoicePanel from './LiveVoicePanel';
import useMicLevel from '../../hooks/useMicLevel';

const STATES = ['muted', 'listening', 'transcribing', 'speaking', 'error'];

export default function VoiceLab() {
  const [state, setState] = useState('listening');
  const [isMuted, setIsMuted] = useState(false);
  const [ccEnabled, setCCEnabled] = useState(true);
  const [manualLevel, setManualLevel] = useState(0.35);
  const [useRealMic, setUseRealMic] = useState(true);

  const micActive = useRealMic && state === 'listening' && !isMuted;
  const { micLevel: liveMicLevel, permissionState } = useMicLevel(micActive);

  const micLevel = useRealMic ? liveMicLevel : manualLevel;

  useEffect(() => {
    if (state === 'muted') {
      setIsMuted(true);
    } else if (isMuted && state !== 'error') {
      setIsMuted(false);
    }
  }, [state]);

  const liveVoiceState = state === 'muted' ? 'muted' : state;

  return (
    <div className="min-h-screen bg-[#081d24] text-white">
      <div className="fixed top-4 left-4 z-[60] w-[320px] rounded-2xl border border-white/10 bg-black/35 backdrop-blur-xl p-4 shadow-2xl">
        <div className="text-sm font-semibold mb-3">Voice Lab</div>

        <label className="block text-xs text-white/70 mb-1">State</label>
        <select
          value={state}
          onChange={(e) => setState(e.target.value)}
          className="w-full rounded-xl bg-white/10 border border-white/10 px-3 py-2 text-sm mb-3"
        >
          {STATES.map((s) => (
            <option key={s} value={s} className="text-black">
              {s}
            </option>
          ))}
        </select>

        <label className="flex items-center justify-between text-sm mb-2">
          <span>Muted</span>
          <input
            type="checkbox"
            checked={isMuted}
            onChange={(e) => {
              const nextMuted = e.target.checked;
              setIsMuted(nextMuted);

              if (nextMuted) {
                setState('muted');
              } else if (state === 'muted') {
                setState('listening');
              }
            }}
          />
        </label>

        <label className="flex items-center justify-between text-sm mb-2">
          <span>CC enabled</span>
          <input
            type="checkbox"
            checked={ccEnabled}
            onChange={(e) => setCCEnabled(e.target.checked)}
          />
        </label>

        <label className="flex items-center justify-between text-sm mb-2">
          <span>Use real mic</span>
          <input
            type="checkbox"
            checked={useRealMic}
            onChange={(e) => setUseRealMic(e.target.checked)}
          />
        </label>

        <label className="block text-xs text-white/70 mt-3 mb-1">
          Mic level: {micLevel.toFixed(2)}{' '}
          {useRealMic ? `(live mic: ${permissionState})` : '(manual)'}
        </label>

        <div className="mt-2 mb-3">
          <div className="h-3 w-full rounded-full bg-white/10 overflow-hidden border border-white/10">
            <div
              className="h-full rounded-full transition-all duration-75"
              style={{
                width: `${Math.max(2, micLevel * 100)}%`,
                background:
                  micLevel > 0.65
                    ? 'linear-gradient(90deg, rgba(255,245,220,0.95), rgba(245,222,179,1))'
                    : micLevel > 0.25
                      ? 'linear-gradient(90deg, rgba(245,222,179,0.9), rgba(255,248,225,0.95))'
                      : 'linear-gradient(90deg, rgba(245,222,179,0.45), rgba(245,222,179,0.75))',
                boxShadow: `0 0 ${8 + micLevel * 22}px rgba(245,222,179,${0.18 + micLevel * 0.55})`,
              }}
            />
          </div>
        </div>

        <input
          type="range"
          min="0"
          max="1"
          step="0.01"
          value={manualLevel}
          onChange={(e) => setManualLevel(Number(e.target.value))}
          disabled={useRealMic}
          className={`w-full ${useRealMic ? 'opacity-40 cursor-not-allowed' : ''}`}
        />

        <div className="mt-4 text-[11px] text-white/55 leading-5">
          {useRealMic
            ? 'Using your real microphone input to drive the orb.'
            : 'Manual mode lets you test the orb without live mic input.'}
        </div>
      </div>

      <LiveVoicePanel
        liveVoiceState={liveVoiceState}
        isMuted={isMuted}
        ccEnabled={ccEnabled}
        micLevel={micLevel}
        onToggleMute={() => {
          const nextMuted = !isMuted;
          setIsMuted(nextMuted);

          if (nextMuted) {
            setState('muted');
          } else if (state === 'muted') {
            setState('listening');
          }
        }}
        onToggleCC={() => setCCEnabled((prev) => !prev)}
        onClose={() => {}}
      />
    </div>
  );
}